import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { noteText } from '../utils/notePresentation';
import { MAX_COVER_CROP_FRACTION, coverCropFraction, fitCover } from '../utils/coverLayout';
import './NoteDetail.css';

export default function NoteDetailHeader({
  note,
  backLabel,
  onBack,
  cover,
  coverAction,
  actions,
  primaryAction,
  primaryActionClassName = '',
  secondaryAction,
  additionalFacts,
  children,
}) {
  const coverRef = useRef(null);
  const [coverLayout, setCoverLayout] = useState({ mode: '', aspectRatio: null });
  const updateCoverMode = useCallback(() => {
    const image = coverRef.current?.querySelector('img');
    if (!image?.naturalWidth || !image.naturalHeight) return;
    const width = coverRef.current?.parentElement?.clientWidth || window.innerWidth;
    const viewportHeight = window.innerHeight;
    const naturalHeight = (image.naturalHeight / image.naturalWidth) * width;
    const isTall = naturalHeight > (viewportHeight * 0.5);
    const contained = coverCropFraction(image.naturalWidth, image.naturalHeight, width, Math.min(naturalHeight, viewportHeight * 0.5)) >= MAX_COVER_CROP_FRACTION;
    const fitted = contained ? fitCover(image.naturalWidth, image.naturalHeight, Math.max(1, width - 32), viewportHeight * 0.5) : null;
    setCoverLayout({
      mode: contained ? 'is-contained' : isTall ? 'is-tall' : 'is-natural',
      aspectRatio: `${image.naturalWidth} / ${image.naturalHeight}`,
      height: Math.min(naturalHeight, viewportHeight * 0.5),
      fitted,
    });
  }, []);

  useEffect(() => {
    updateCoverMode();
    window.addEventListener('resize', updateCoverMode);
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateCoverMode) : null;
    if (coverRef.current?.parentElement) observer?.observe(coverRef.current.parentElement);
    return () => { window.removeEventListener('resize', updateCoverMode); observer?.disconnect(); };
  }, [updateCoverMode]);

  return (
    <div className="note-detail-overview">
      <div className="note-detail-nav">
        <button type="button" className="note-detail-back" onClick={onBack} aria-label={backLabel}>
          <ArrowLeft size={20} aria-hidden="true" /><span>{backLabel}</span>
        </button>
        {actions && <div className="note-detail-top-actions">{actions}</div>}
      </div>
      <div className="note-detail-main">
        <div
          ref={coverRef}
          className={`note-detail-cover ${coverLayout.mode}${coverLayout.aspectRatio ? ' has-measured-cover' : ''}`.trim()}
          style={coverLayout.aspectRatio ? {
            '--note-detail-cover-aspect-ratio': coverLayout.aspectRatio,
            '--note-detail-cover-height': `${coverLayout.height}px`,
            ...(coverLayout.fitted || {}),
          } : undefined}
          onLoadCapture={updateCoverMode}
        >
          {cover}{coverAction}
        </div>
        <section className="note-detail-copy" aria-label={note.title}>
          <div className="note-detail-title-row"><h1>{note.title}</h1></div>
          {note.description && <p className="note-detail-description">{note.description}</p>}
          <div className="note-detail-facts">
            <span>{noteText('cardCount', { count: note.cardCount })}</span>
            {note.difficulty && <span>{noteText('difficulty', { level: note.difficulty })}</span>}
            {(note.questionLocale || note.learningLocale) && <span title={noteText('languages')}>{[note.questionLocale, note.learningLocale].filter(Boolean).join(' → ')}</span>}
            {additionalFacts}
          </div>
        </section>
      </div>
      {(primaryAction || secondaryAction) && (
        <div className="note-detail-action-row">
          {primaryAction && <div className={`note-detail-primary-action ${primaryActionClassName}`.trim()}>{primaryAction}</div>}
          {secondaryAction && <div className="note-detail-secondary-action">{secondaryAction}</div>}
        </div>
      )}
      {children && <div className="note-detail-extra">{children}</div>}
    </div>
  );
}
